import { useState } from 'react';
import styled from 'styled-components';
import Container from '@adecco/base-app/src/components/atoms/Container/Container';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import Paragraph from '@adecco/base-app/src/components/atoms/Paragraph/Paragraph';
import { contacttext, inputtext, placeholdertext } from '@adecco/base-app/src/styles/typography';
import Button from '@adecco/base-app/src/components/atoms/Button/Button';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import { useAppContext } from '@adecco/base-app/src/utilities/appProvider/appProvider';

type TabProps = {
  title: string;
  content: string;
  linkUrl?: string | undefined;
};

export interface ITabsModule {
  headline: string;
  subHeadline: string;
  tabContents: ReadonlyArray<TabProps>;
  centeredHeadline?: boolean;
}

const HeadlineWrapper = styled.div`
  margin-bottom: ${calcrem(30)};

  ${moreThan('md')} {
    margin-bottom: ${calcrem(30)};
  }
`;

const TabGroup = styled.div`
  display: none;

  ${moreThan('md')} {
    display: flex;
    flex-wrap: wrap;
  }
`;

const Tab = styled.button<{
  active: boolean;
}>`
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  font-size: 20px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.text};
  margin-right: 10px;
  padding: 10px 15px;
  cursor: pointer;
  background: linear-gradient(-180deg, rgba(238, 238, 238, 0) 0%, rgba(216, 216, 216, 0.16) 100%);
  border: 1px solid ${(props) => props.theme.colors.medGrey};
  border-radius: ${({ theme }) => theme.radius.tab};
  border-bottom-style: none;
  outline: 0;

  ${({ active }) =>
    active &&
    `
    border: 1px solid transparent;
    box-shadow: none;
    box-shadow: -4px -4px 6px 0px rgba(0, 0, 0, 0.07);
    background: white;
    `}
`;

const Content = styled.div<{
  isActive: boolean;
}>`
  display: ${({ isActive }) => (isActive === true ? 'block' : 'none')};
  ${moreThan('md')} {
    box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.1);
    padding: 5%;
    min-height: 200px;
    background-color: ${(props) => props.theme.colors.white};
  }
`;

const StyledParagraph = styled(Paragraph)`
  ${contacttext};
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  color: #000;
  border: 1px solid #000;
`;

const Select = styled.select`
  ${inputtext};
  margin-bottom: ${calcrem(30)};
  width: 100%;
  border-radius: ${(props) => props.theme.radius.input};
  border: 1px solid ${(props) => props.theme.colors.medGrey};
  box-shadow: ${(props) => props.theme.boxShadow.materialMid};
  padding: 12px 14px;
  height: 46px;

  &::placeholder {
    ${placeholdertext}
  }

  &:focus {
    background-color: ${(props) => props.theme.colors.lighterGrey};
    outline: none;
  }

  ${moreThan('md')} {
    display: none;
  }
`;

const HeadlineStyled = styled(Headline)`
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  font-size: ${(props) => props.theme.fontSizes.headline2};
  line-height: ${(props) => props.theme.lineHeights.headline1};

  ${moreThan('md')} {
    font-size: ${(props) => props.theme.fontSizes.headline2WideScreen};
    line-height: ${(props) => props.theme.lineHeights.tabsModuleHeadlineWideScreen};
  }
`;

const SublineStyled = styled(Headline)`
  font-size: ${(props) => props.theme.fontSizes.subline};
  line-height: ${(props) => props.theme.lineHeights.subline};

  ${moreThan('md')} {
    font-size: ${(props) => props.theme.fontSizes.sublineWideScreen};
    line-height: ${(props) => props.theme.lineHeights.sublineWideScreen};
  }
`;

const TabsModule: React.FC<ITabsModule> = ({
  headline,
  subHeadline,
  tabContents,
  centeredHeadline,
}) => {
  const [indexArray, setIndexArray] = useState(0);
  const { t } = useAppContext();

  return (
    <Container>
      <HeadlineWrapper>
        <HeadlineStyled skin={2} centered={centeredHeadline}>
          {headline}
        </HeadlineStyled>
        <SublineStyled skin={3} centered={centeredHeadline}>
          {subHeadline}
        </SublineStyled>
      </HeadlineWrapper>
      <TabGroup>
        {tabContents.map((data, index) => (
          <Tab
            key={index}
            active={indexArray === index}
            onClick={() => {
              setIndexArray(index);
            }}
            type="button"
          >
            {data.title}
          </Tab>
        ))}
      </TabGroup>
      <Select
        name="topics"
        id="topics-select"
        onChange={(event) => setIndexArray(parseInt(event.target.value, 10))}
      >
        {tabContents.map((data, index) => (
          <option key={index} value={index}>
            {data.title}
          </option>
        ))}
      </Select>
      {tabContents.map((tabContent, index) => (
        <Content key={index} isActive={index === indexArray}>
          <StyledParagraph noTransform>{tabContent.content}</StyledParagraph>
          <br />
          {tabContent.linkUrl && (
            <StyledButton variant="small" href={tabContent.linkUrl}>
              {t('general-learn-more')}
            </StyledButton>
          )}
        </Content>
      ))}
    </Container>
  );
};

export default TabsModule;
